import React, { useState, useEffect, useRef } from 'react';
import { Home } from 'lucide-react';
import { API_BASE } from '../../config';
import { useTranslation } from '../../i18n/LanguageContext';
import { useAuth } from '../../App';
import './TeamCalendar.css';

const KNOWN_SHIFT_CODES = ['M', 'J', 'AM', 'N'];
const DEPT_TRANSLATION_KEYS = {
  'Pharmacie': 'dept_pharmacy',
  'Pharmacy': 'dept_pharmacy',
  'Achats': 'dept_achats',
  'Direction': 'dept_direction',
  'Management': 'dept_direction',
  'Ressources Humaines': 'dept_ressources_humaines',
  'Human Resources': 'dept_ressources_humaines',
  'Informatique': 'dept_informatique',
  'Information Technology': 'dept_informatique',
  'IT': 'dept_informatique',
  'Qualité': 'dept_qualite',
  'Quality': 'dept_qualite',
  'Commercial': 'dept_commercial',
  'Sales': 'dept_commercial',
  'Administration': 'dept_administration',
  'Production': 'dept_production'
};

function TeamCalendar({ onNavigate }) {
  const { t, language } = useTranslation();
  const dateLocale = language === 'en' ? 'en-US' : 'fr-FR';
  const { user } = useAuth();

  const getShiftDisplayName = (shift) => {
    const code = (shift && shift.code) || (shift && shift.shift_code);
    if (code && KNOWN_SHIFT_CODES.includes(code)) {
      const key = `planning.shift_${code}`;
      const translated = t(key);
      return translated !== key ? translated : (shift && (shift.name || shift.shift_name)) || '';
    }
    return (shift && (shift.name || shift.shift_name)) || '';
  };

  const translateDepartment = (dept) => {
    if (!dept) return '';
    const key = DEPT_TRANSLATION_KEYS[dept];
    return key ? t(`planning.${key}`) : dept;
  };
  const canEdit = user && ['admin', 'manager'].includes(user.role);
  const [assignments, setAssignments] = useState([]);
  const [shiftTypes, setShiftTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [showShiftModal, setShowShiftModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedEmployeeFilter, setSelectedEmployeeFilter] = useState('');
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedDates, setSelectedDates] = useState([]);
  const [editingAssignment, setEditingAssignment] = useState(null); // when clicking existing shift (any type) to edit
  const [departments, setDepartments] = useState([]);
  const [allEmployeesList, setAllEmployeesList] = useState([]);

  // Formater date en local (YYYY-MM-DD) — évite le décalage timezone de toISOString()
  const toLocalDateString = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  // Date de début de la semaine (lundi)
  const getMonday = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Ajuster pour lundi = 1
    return new Date(d.setDate(diff));
  };
  
  const [startDate, setStartDate] = useState(() => getMonday(new Date()));
  
  // Générer les 7 jours — noon local pour éviter timezone/midnight edge cases
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    return new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + i, 12, 0, 0);
  });
  
  useEffect(() => {
    loadData();
  }, [startDate, selectedDepartment, selectedEmployeeFilter]);
  
  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 6);
      
      const startDateStr = toLocalDateString(startDate);
      const endDateStr = toLocalDateString(endDate);
      const calendarUrl = `${API_BASE}/api/planning/calendar?start_date=${startDateStr}&end_date=${endDateStr}${selectedDepartment ? `&department=${encodeURIComponent(selectedDepartment)}` : ''}`;
      const headers = { Authorization: `Bearer ${token}` };
      const [assignmentsRes, typesRes] = await Promise.all([
        fetch(calendarUrl, { headers }),
        fetch(`${API_BASE}/api/planning/shift-types`, { headers })
      ]);
      if (assignmentsRes.ok && typesRes.ok) {
        const [assignmentsData, typesData] = await Promise.all([
          assignmentsRes.json(),
          typesRes.json()
        ]);
        
        setAssignments(assignmentsData.assignments || []);
        setShiftTypes(typesData.shiftTypes || []);
        
        // Priorité: utiliser allEmployees du backend si disponible
        let employeesArray = [];
        if (assignmentsData.allEmployees && Array.isArray(assignmentsData.allEmployees)) {
          employeesArray = assignmentsData.allEmployees;
          console.log('✅ Planning: Utilisation allEmployees du backend:', employeesArray.length);
        } else {
          // Fallback: utiliser les employés qui ont des assignments
          console.log('⚠️ Planning: allEmployees non disponible, utilisation des employés des assignments');
          employeesArray = assignmentsData.assignments 
            ? [...new Map(assignmentsData.assignments.map(a => [a.employee_id, {
              id: a.employee_id,
              first_name: a.first_name,
              last_name: a.last_name,
              employee_code: a.employee_code,
              department: a.department
            }])).values()]
            : [];
        }
          
        // Afficher TOUS les employés actifs
        const activeEmployees = employeesArray;
        
        // Stocker tous les employés pour les filtres
        setAllEmployeesList(activeEmployees);
        
        if (activeEmployees.length === 0) {
          setMessage(t('planning.no_active_employees'));
        } else {
          setMessage('');
        }
        
        // Extraire la liste unique des départements
        const uniqueDepartments = [...new Set(activeEmployees
          .map(e => e.department)
          .filter(d => d && d.trim() !== '')
        )].sort();
        setDepartments(uniqueDepartments);
        
        // Appliquer les filtres
        let filteredEmployees = activeEmployees;
        if (selectedDepartment) {
          filteredEmployees = filteredEmployees.filter(e => e.department === selectedDepartment);
        }
        if (selectedEmployeeFilter) {
          filteredEmployees = filteredEmployees.filter(e => e.id === selectedEmployeeFilter);
        }
        
        setEmployees(filteredEmployees);
        
        // Debug log pour vérifier les données
        console.log('📊 Planning - Assignments chargés:', assignmentsData.assignments?.length || 0);
        console.log('📊 Planning - Employés totaux:', activeEmployees.length);
        console.log('📊 Planning - Employés filtrés:', filteredEmployees.length);
        console.log('📊 Planning - Départements:', uniqueDepartments);
        console.log('📊 Planning - Types de shifts:', typesData.shiftTypes?.length || 0);
      } else {
        // Gérer les erreurs
        let errorMessages = [];
        if (!assignmentsRes.ok) {
          const errorText = await assignmentsRes.text();
          console.error('❌ Erreur chargement planning:', assignmentsRes.status, errorText);
          errorMessages.push(`Planning: ${assignmentsRes.status === 403 ? 'Accès refusé' : 'Erreur ' + assignmentsRes.status}`);
        }
        if (!typesRes.ok) {
          const errorText = await typesRes.text();
          console.error('❌ Erreur chargement types:', typesRes.status, errorText);
          errorMessages.push(`Types: ${typesRes.status === 403 ? 'Accès refusé' : 'Erreur ' + typesRes.status}`);
        }
        
        if (errorMessages.length > 0) {
          setMessage(t('planning.error_details', { details: errorMessages.join(', ') }));
        } else {
          setMessage(t('planning.error_loading'));
        }
        
        // Même en cas d'erreur, essayer d'utiliser les données partielles
        let partialEmployees = [];
        try {
          if (assignmentsRes.ok) {
            const assignmentsData = await assignmentsRes.json();
            setAssignments(assignmentsData.assignments || []);
            if (assignmentsData.allEmployees && Array.isArray(assignmentsData.allEmployees)) {
              partialEmployees = assignmentsData.allEmployees;
              setAllEmployeesList(partialEmployees);
              setEmployees(partialEmployees);
            }
          }
        } catch (e) {
          console.error('Erreur parsing assignments:', e);
        }
        
        try {
          if (typesRes.ok) {
            const typesData = await typesRes.json();
            setShiftTypes(typesData.shiftTypes || []);
          }
        } catch (e) {
          console.error('Erreur parsing types:', e);
        }
        
        // Si aucune donnée partielle disponible, réinitialiser
        if (partialEmployees.length === 0) {
          setEmployees([]);
          setAllEmployeesList([]);
        }
      }
    } catch (error) {
      console.error('Erreur chargement données:', error);
      setMessage(t('planning.error_loading'));
    } finally {
      setLoading(false);
    }
  };
  
  // Normaliser une date du backend en YYYY-MM-DD (local = même jour que le calendrier, pas de décalage timezone)
  const normalizeBackendDate = (val) => {
    if (val == null) return '';
    if (typeof val === 'string') return val.split('T')[0].split(' ')[0].substring(0, 10);
    if (val instanceof Date) return `${val.getUTCFullYear()}-${String(val.getUTCMonth() + 1).padStart(2, '0')}-${String(val.getUTCDate()).padStart(2, '0')}`;
    return String(val).split('T')[0].split(' ')[0].substring(0, 10);
  };

  const getShiftForEmployeeAndDate = (employeeId, date) => {
    // Date du calendrier (local) en YYYY-MM-DD
    const dateStr = toLocalDateString(date);
    
    const shift = assignments.find(a => {
      if (a.employee_id !== employeeId) return false;
      const assignmentDate = normalizeBackendDate(a.shift_date);
      return assignmentDate === dateStr;
    });
    
    return shift;
  };
  
  const handlePreviousWeek = () => {
    const newDate = new Date(startDate);
    newDate.setDate(newDate.getDate() - 7);
    setStartDate(getMonday(newDate));
  };
  
  const handleNextWeek = () => {
    const newDate = new Date(startDate);
    newDate.setDate(newDate.getDate() + 7);
    setStartDate(getMonday(newDate));
  };
  
  const handleAddShift = (employeeId, date, existingShift = null) => {
    // Vérifier les permissions
    if (!canEdit) {
      setMessage(t('planning.read_only_mode') || 'Mode consultation uniquement');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    
    if (shiftTypes.length === 0) {
      setMessage(t('planning.error_create_shifts_first'));
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    
    // Clic sur un shift existant (M, J, AM, N) → ouvrir le modal pour modifier / voir (form update pour tous les types)
    if (existingShift) {
      setEditingAssignment({ id: existingShift.id, employee_id: employeeId, shift_date: toLocalDateString(date), shift_type_id: existingShift.shift_type_id });
      setSelectedEmployee(employeeId);
      setSelectedDate(date);
      setSelectedDates([date]);
      setShowShiftModal(true);
      return;
    }
    
    // Mode multi-sélection
    if (multiSelectMode) {
      // S'assurer que l'employé est défini (première sélection ou même employé)
      if (!selectedEmployee || selectedEmployee !== employeeId) {
        // Si on change d'employé, réinitialiser les sélections
        setSelectedEmployee(employeeId);
        setSelectedDates([date]);
      } else {
        // Même employé, ajouter/retirer la date
        const dateStr = toLocalDateString(date);
        const dateIndex = selectedDates.findIndex(d => toLocalDateString(d) === dateStr);
        
        if (dateIndex >= 0) {
          // Désélectionner
          setSelectedDates(selectedDates.filter((_, i) => i !== dateIndex));
          // Si plus de dates sélectionnées, réinitialiser l'employé
          if (selectedDates.length === 1) {
            setSelectedEmployee(null);
          }
        } else {
          // Sélectionner
          setSelectedDates([...selectedDates, date]);
        }
      }
      return;
    }
    
    // Mode normal (un seul jour) — pas d'édition, on ajoute
    setEditingAssignment(null);
    setSelectedEmployee(employeeId);
    setSelectedDate(date);
    setSelectedDates([date]);
    setShowShiftModal(true);
  };

  const handleSelectShiftType = async (shiftTypeId) => {
    const shiftTypeIdStr = (shiftTypeId ?? '').toString();
    console.log('🔧 handleSelectShiftType appelé avec:', {
      shiftTypeId: shiftTypeIdStr,
      selectedEmployee,
      selectedDatesCount: selectedDates.length,
      editingAssignment: editingAssignment?.id,
      multiSelectMode
    });
    
    // Édition d'un shift existant (clic sur M, J, AM ou N → modal → choix type)
    if (editingAssignment && editingAssignment.id) {
      setShowShiftModal(false);
      setEditingAssignment(null);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE}/api/planning/assignments/${editingAssignment.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ shift_type_id: shiftTypeIdStr })
        });
        if (response.ok) {
          setMessage('✅ ' + (t('planning.success_updated') || 'Shift mis à jour'));
          setTimeout(() => setMessage(''), 3000);
          await loadData();
        } else {
          const err = await response.json().catch(() => ({}));
          setMessage('❌ ' + (err.error || t('planning.error_assigning')));
          setTimeout(() => setMessage(''), 5000);
        }
      } catch (err) {
        console.error('❌ Erreur mise à jour shift:', err);
        setMessage('❌ ' + (t('planning.error_assigning') || err.message));
        setTimeout(() => setMessage(''), 5000);
      }
      return;
    }
    
    if (!selectedEmployee || selectedDates.length === 0) {
      console.warn('⚠️ Impossible d\'assigner: selectedEmployee ou selectedDates manquants', {
        selectedEmployee,
        selectedDatesCount: selectedDates.length
      });
      setMessage('❌ ' + t('planning.error_employee_dates_not_selected'));
      setTimeout(() => setMessage(''), 5000);
      return;
    }
    
    console.log('🔄 Assignation en cours...', {
      employeeId: selectedEmployee,
      shiftTypeId: shiftTypeIdStr,
      dates: selectedDates.map(d => toLocalDateString(d))
    });
    
    setShowShiftModal(false);
    
    try {
      const token = localStorage.getItem('token');
      
      // Assigner pour chaque date sélectionnée (date locale, pas UTC) — ids sont des UUID strings
      const employeeIdStr = (selectedEmployee ?? '').toString();
      if (!employeeIdStr || !shiftTypeIdStr) {
        setMessage('❌ ' + (t('planning.error_assigning') || 'Invalid employee or shift type.'));
        setTimeout(() => setMessage(''), 5000);
        return;
      }
      const assignments = selectedDates.map(date => ({
        employee_id: employeeIdStr,
        shift_type_id: shiftTypeIdStr,
        shift_date: toLocalDateString(date)
      }));
      
      console.log('📤 Envoi de la requête bulk:', assignments);
      const response = await fetch(`${API_BASE}/api/planning/assignments/bulk`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ assignments })
      });
      
      console.log('📥 Réponse reçue:', response.status, response.statusText);
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ Résultat:', result);
        const assignedCount = result.assignments?.length || 0;
        
        if (assignedCount === 0) {
          setMessage('⚠️ ' + t('planning.warning_no_shifts_assigned'));
          setTimeout(() => setMessage(''), 5000);
        } else {
          setMessage('✅ ' + t('planning.success_shifts_assigned').replace('{{count}}', assignedCount));
          setTimeout(() => setMessage(''), 3000);
        }
        
        // Réinitialiser les sélections
        setSelectedEmployee(null);
        setSelectedDate(null);
        setSelectedDates([]);
        
        // Recharger les données pour voir les nouveaux shifts
        console.log('🔄 Rechargement des données...');
        try {
          await loadData();
          console.log('✅ Données rechargées avec succès');
        } catch (reloadError) {
          console.error('❌ Erreur lors du rechargement:', reloadError);
          setMessage('✅ ' + t('planning.success_assigned_reload_error'));
          setTimeout(() => setMessage(''), 5000);
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Erreur réponse:', response.status, errorText);
        let errorMessage = t('planning.error_assigning');
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }
        setMessage('❌ ' + (errorMessage === t('planning.error_assigning') ? errorMessage : t('planning.error_assigning_unknown').replace('{{error}}', errorMessage)));
        setTimeout(() => setMessage(''), 5000);
      }
    } catch (error) {
      console.error('❌ Erreur assignation shift:', error);
      setMessage('❌ ' + t('planning.error_assigning_unknown').replace('{{error}}', error.message || t('leaves.unknown_error')));
      setTimeout(() => setMessage(''), 5000);
    }
  };
  
  // Filtrer les employés par département
  const filteredEmployees = selectedDepartment 
    ? employees.filter(e => e.department === selectedDepartment)
    : employees;
  
  const handleDeleteShift = async (assignmentId) => {
    // Vérifier les permissions
    if (!canEdit) {
      setMessage(t('planning.read_only_mode') || 'Mode consultation uniquement');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    
    if (!window.confirm(t('planning.delete_confirm'))) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/planning/assignments/${assignmentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        setMessage('✅ ' + t('planning.success_deleted'));
        await loadData();
        setTimeout(() => setMessage(''), 3000);
      } else {
        const error = await response.json();
        setMessage('❌ ' + (error.error ? t('planning.error_delete').replace('{{error}}', error.error) : t('planning.error_delete_unknown')));
      }
    } catch (error) {
      setMessage('❌ ' + t('planning.error_delete_generic'));
    }
  };
  
  // Fonction d'export PDF
  const handleExportPDF = async () => {
    try {
      const token = localStorage.getItem('token');
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 6);
      
      const startDateStr = toLocalDateString(startDate);
      const endDateStr = toLocalDateString(endDate);
      
      const response = await fetch(
        `${API_BASE}/api/planning/export/pdf?start_date=${startDateStr}&end_date=${endDateStr}${selectedDepartment ? `&department=${encodeURIComponent(selectedDepartment)}` : ''}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `planning-${startDateStr}-${endDateStr}.pdf`;
        document.body.appendChild(a);
        try {
          a.click();
        } finally {
          try { window.URL.revokeObjectURL(url); } catch (e) {}
          try { if (a && a.parentNode) a.parentNode.removeChild(a); } catch (err) { console.warn('Could not remove temporary download link:', err); }
        }
        setMessage('✅ ' + t('planning.success_pdf_downloaded'));
        setTimeout(() => setMessage(''), 3000);
      } else {
        const error = await response.json();
        setMessage('❌ ' + (error.error ? t('planning.error_pdf_generation').replace('{{error}}', error.error) : t('planning.error_pdf_unknown')));
      }
    } catch (error) {
      console.error('Erreur export PDF:', error);
      setMessage('❌ ' + t('planning.error_pdf_generic'));
    }
  };
  
  // Fonction d'export Excel
  const handleExportExcel = async () => {
    try {
      const token = localStorage.getItem('token');
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 6);
      
      const startDateStr = toLocalDateString(startDate);
      const endDateStr = toLocalDateString(endDate);
      
      const response = await fetch(
        `${API_BASE}/api/planning/export/excel?start_date=${startDateStr}&end_date=${endDateStr}${selectedDepartment ? `&department=${encodeURIComponent(selectedDepartment)}` : ''}${selectedEmployeeFilter ? `&employee_id=${encodeURIComponent(selectedEmployeeFilter)}` : ''}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `planning-${startDateStr}-${endDateStr}.xlsx`;
        document.body.appendChild(a);
        try {
          a.click();
        } finally {
          try { window.URL.revokeObjectURL(url); } catch (e) {}
          try { if (a && a.parentNode) a.parentNode.removeChild(a); } catch (err) { console.warn('Could not remove temporary download link:', err); }
        }
        setMessage('✅ ' + t('planning.success_excel_downloaded'));
        setTimeout(() => setMessage(''), 3000);
      } else {
        const error = await response.json();
        setMessage('❌ ' + (error.error ? t('planning.error_excel_generation').replace('{{error}}', error.error) : t('planning.error_excel_unknown')));
      }
    } catch (error) {
      console.error('Erreur export Excel:', error);
      setMessage('❌ ' + t('planning.error_excel_generic'));
    }
  };
  
  // Fonction d'envoi WhatsApp
  const handleSendWhatsApp = () => {
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 6);
    
    const startDateStr = startDate.toLocaleDateString(navigator.language || 'fr-FR');
    const endDateStr = endDate.toLocaleDateString(navigator.language || 'fr-FR');
    
    // Créer un résumé textuel du planning
    let summary = `📅 ${t('planning.team_planning')}\n`;
    summary += `${t('planning.period')}: ${t('planning.from_to').replace('{{start}}', startDateStr).replace('{{end}}', endDateStr)}\n`;
    if (selectedDepartment) {
      summary += `${t('planning.department_label').replace('{{dept}}', selectedDepartment)}\n`;
    }
    summary += `\n`;
    
    filteredEmployees.forEach(employee => {
      summary += `👤 ${employee.firstName || employee.first_name} ${employee.lastName || employee.last_name}\n`;
      weekDays.forEach(day => {
        const shift = getShiftForEmployeeAndDate(employee.id, day);
        const dayName = day.toLocaleDateString(navigator.language || 'fr-FR', { weekday: 'short', day: 'numeric' });
        if (shift) {
          summary += `  ${dayName}: ${shift.shift_name} (${shift.shift_start} - ${shift.shift_end})\n`;
        } else {
          summary += `  ${dayName}: - \n`;
        }
      });
      summary += `\n`;
    });
    
    // Encoder le message pour WhatsApp
    const encodedMessage = encodeURIComponent(summary);
    const whatsappUrl = `https://wa.me/?text=${encodedMessage}`;
    
    // Ouvrir WhatsApp dans une nouvelle fenêtre
    window.open(whatsappUrl, '_blank');
    setMessage('✅ ' + t('planning.success_whatsapp_opened'));
    setTimeout(() => setMessage(''), 3000);
  };
  
  if (loading) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center', color: '#fff' }}>
        <div>{t('planning.loading')}</div>
      </div>
    );
  }

  return (
    <div className="team-calendar">
      <div className="team-calendar-content">
        <div className="calendar-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {onNavigate && (
              <button
                onClick={() => onNavigate('home')}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: '#ffffff',
                  padding: '10px',
                  borderRadius: '12px',
                  fontSize: '18px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s',
                  backdropFilter: 'blur(10px)',
                  width: '44px',
                  height: '44px',
                  flexShrink: 0
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                title={t('planning.back_to_home')}
              >
                <Home size={20} />
              </button>
            )}
            <button onClick={handlePreviousWeek} className="nav-button">
              ← {t('planning.previous_week')}
            </button>
          </div>
          <h2 style={{ color: '#fff', margin: 0 }}>
            {t('planning.week_of')} {startDate.toLocaleDateString(dateLocale, { day: 'numeric', month: 'long', year: 'numeric' })}
          </h2>
          <button onClick={handleNextWeek} className="nav-button">
            {t('planning.next_week')} →
          </button>
        </div>
      
      {message && (
        <div style={{
          background: (message.includes('Erreur') || message.includes('Error')) ? 'rgba(239, 68, 68, 0.2)' : 'rgba(16, 185, 129, 0.2)',
          color: (message.includes('Erreur') || message.includes('Error')) ? '#fca5a5' : '#6ee7b7',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: `1px solid ${(message.includes('Erreur') || message.includes('Error')) ? 'rgba(239, 68, 68, 0.3)' : 'rgba(16, 185, 129, 0.3)'}`
        }}>
          {message}
        </div>
      )}
      
      {/* Barre d'outils avec filtres et actions */}
      <div style={{
        display: 'flex',
        gap: '16px',
        marginBottom: '20px',
        flexWrap: 'wrap',
        alignItems: 'center',
        padding: '16px',
        background: 'rgba(255,255,255,0.05)',
        borderRadius: '12px',
        border: '1px solid rgba(255,255,255,0.1)'
      }}>
        {/* Filtres */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', flex: '1' }}>
          {departments.length > 0 && (
            <>
              <label style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', fontWeight: 500, whiteSpace: 'nowrap' }}>
                {t('planning.department')}:
              </label>
              <select
                value={selectedDepartment}
                onChange={(e) => {
                  setSelectedDepartment(e.target.value);
                  // Réinitialiser le filtre employé si le département change et que l'employé n'est pas dans ce département
                  if (selectedEmployeeFilter && e.target.value !== '') {
                    const selectedEmp = allEmployeesList.find(emp => emp.id === selectedEmployeeFilter);
                    if (selectedEmp && selectedEmp.department !== e.target.value) {
                      setSelectedEmployeeFilter('');
                    }
                  }
                }}
                style={{
                  padding: '8px 12px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '14px',
                  cursor: 'pointer',
                  minWidth: '180px'
                }}
              >
                <option value="">{t('planning.all_departments')}</option>
                {departments.map(dept => (
                  <option key={dept} value={dept} style={{ background: '#1a1f3a', color: '#fff' }}>
                    {translateDepartment(dept)}
                  </option>
                ))}
              </select>
            </>
          )}
          
          <label style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', fontWeight: 500, whiteSpace: 'nowrap' }}>
            {t('planning.employee')}:
          </label>
          <select
            value={selectedEmployeeFilter}
            onChange={(e) => setSelectedEmployeeFilter(e.target.value)}
            style={{
              padding: '8px 12px',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '8px',
              color: '#fff',
              fontSize: '14px',
              cursor: 'pointer',
              minWidth: '220px'
            }}
          >
            <option value="">{t('planning.all_employees')}</option>
            {allEmployeesList
              .filter(emp => !selectedDepartment || emp.department === selectedDepartment)
              .map(emp => (
                <option key={emp.id} value={emp.id} style={{ background: '#1a1f3a', color: '#fff' }}>
                  {emp.firstName || emp.first_name} {emp.lastName || emp.last_name}
                  {emp.department ? ` (${translateDepartment(emp.department)})` : ''}
                </option>
              ))}
          </select>
          
          {(selectedDepartment || selectedEmployeeFilter) && (
            <button
              onClick={() => {
                setSelectedDepartment('');
                setSelectedEmployeeFilter('');
              }}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.3)',
                background: 'rgba(239, 68, 68, 0.2)',
                color: '#fca5a5',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.3)';
                e.currentTarget.style.border = '1px solid rgba(239, 68, 68, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
                e.currentTarget.style.border = '1px solid rgba(255,255,255,0.3)';
              }}
            >
              ✕ {t('planning.reset')}
            </button>
          )}
        </div>
        
        {/* Mode multi-sélection - Seulement pour admin/manager */}
        {canEdit && (
          <button
            onClick={() => {
              setMultiSelectMode(!multiSelectMode);
              setSelectedDates([]);
            }}
            style={{
              padding: '10px 20px',
              background: multiSelectMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(255, 255, 255, 0.1)',
              border: `1px solid ${multiSelectMode ? '#3B82F6' : 'rgba(255, 255, 255, 0.2)'}`,
              borderRadius: '10px',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600
            }}
          >
            {multiSelectMode ? '✓ ' + t('planning.multi_day_mode_active') : '📅 ' + t('planning.multi_day_mode')}
          </button>
        )}
        
        {/* Export PDF */}
        <button
          onClick={handleExportPDF}
          style={{
            padding: '10px 20px',
            background: 'rgba(16, 185, 129, 0.2)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: '10px',
            color: '#6ee7b7',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 600
          }}
        >
          📄 {t('planning.export_pdf')}
        </button>
        
        {/* Export Excel */}
        <button
          onClick={handleExportExcel}
          style={{
            padding: '10px 20px',
            background: 'rgba(139, 92, 246, 0.2)',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '10px',
            color: '#c4b5fd',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 600
          }}
        >
          📊 {t('planning.export_excel')}
        </button>
        
        {/* Envoi WhatsApp */}
        <button
          onClick={handleSendWhatsApp}
          style={{
            padding: '10px 20px',
            background: 'rgba(34, 197, 94, 0.2)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: '10px',
            color: '#86efac',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 600
          }}
        >
          💬 {t('planning.send_whatsapp')}
        </button>
      </div>
      
      {multiSelectMode && selectedDates.length > 0 && selectedEmployee && (
        <div style={{
          background: 'rgba(59, 130, 246, 0.2)',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          borderRadius: '10px',
          padding: '12px',
          marginBottom: '20px',
          color: '#93c5fd',
          fontSize: '14px'
        }}>
          <div style={{ marginBottom: '8px', fontWeight: 600 }}>
            📅 {t('planning.days_selected_for').replace('{{count}}', selectedDates.length)} {employees.find(e => e.id === selectedEmployee)?.firstName || employees.find(e => e.id === selectedEmployee)?.first_name || ''} {employees.find(e => e.id === selectedEmployee)?.lastName || employees.find(e => e.id === selectedEmployee)?.last_name || ''}
          </div>
          <div style={{ fontSize: '12px', opacity: 0.9 }}>
            {t('planning.click_legend_to_assign')}
          </div>
        </div>
      )}
      
      <div className="calendar-legend">
        <div style={{ color: '#fff', marginBottom: '8px', fontWeight: 600 }}>
          {t('planning.legend_shifts')}:
          {multiSelectMode && selectedDates.length > 0 && selectedEmployee && (
            <span style={{ fontSize: '12px', fontWeight: 400, opacity: 0.8, marginLeft: '12px' }}>
              {t('planning.click_type_to_assign')}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
          {shiftTypes.map(type => (
            <div 
              key={type.id} 
              className="legend-item"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('🖱️ Clic sur type de shift:', {
                  shiftTypeId: type.id,
                  shiftName: type.name,
                  multiSelectMode,
                  selectedDatesCount: selectedDates.length,
                  selectedEmployee
                });
                if (multiSelectMode && selectedDates.length > 0 && selectedEmployee) {
                  console.log('✅ Conditions OK, appel handleSelectShiftType');
                  handleSelectShiftType(type.id);
                } else {
                  console.warn('❌ Conditions non remplies:', {
                    multiSelectMode,
                    selectedDates: selectedDates.length,
                    selectedEmployee
                  });
                }
              }}
              style={{
                cursor: (multiSelectMode && selectedDates.length > 0 && selectedEmployee) ? 'pointer' : 'default',
                padding: (multiSelectMode && selectedDates.length > 0 && selectedEmployee) ? '8px 12px' : '0',
                borderRadius: (multiSelectMode && selectedDates.length > 0 && selectedEmployee) ? '8px' : '0',
                background: (multiSelectMode && selectedDates.length > 0 && selectedEmployee) ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                border: (multiSelectMode && selectedDates.length > 0 && selectedEmployee) ? `1px solid ${type.color}33` : 'none',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                if (multiSelectMode && selectedDates.length > 0 && selectedEmployee) {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.border = `1px solid ${type.color}66`;
                  e.currentTarget.style.transform = 'scale(1.05)';
                }
              }}
              onMouseLeave={(e) => {
                if (multiSelectMode && selectedDates.length > 0 && selectedEmployee) {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                  e.currentTarget.style.border = `1px solid ${type.color}33`;
                  e.currentTarget.style.transform = 'scale(1)';
                }
              }}
            >
              <span 
                className="legend-color" 
                style={{ backgroundColor: type.color }}
              />
              <span style={{ color: '#fff' }}>{getShiftDisplayName(type)} ({type.code})</span>
              <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px' }}>
                {type.start_time} - {type.end_time}
              </span>
            </div>
          ))}
          {shiftTypes.length === 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <span style={{ color: '#fca5a5' }}>
                {t('planning.no_shift_types')}
              </span>
              {canEdit && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      setMessage('');
                      const token = localStorage.getItem('token');
                      const res = await fetch(`${API_BASE}/api/planning/shift-types/create-defaults`, {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      const data = res.ok ? await res.json() : {};
                      if (res.ok) {
                        setShiftTypes(data.shiftTypes || []);
                        setMessage('✅ ' + (t('planning.create_default_shifts_success') || 'Default shifts created.'));
                        setTimeout(() => setMessage(''), 3000);
                        await loadData();
                      } else {
                        setMessage('❌ ' + (data.error || t('planning.create_default_shifts_error') || 'Could not create default shifts.'));
                        setTimeout(() => setMessage(''), 5000);
                      }
                    } catch (err) {
                      setMessage('❌ ' + (t('planning.create_default_shifts_error') || 'Could not create default shifts.'));
                      setTimeout(() => setMessage(''), 5000);
                    }
                  }}
                  style={{
                    padding: '8px 16px',
                    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {t('planning.create_default_shifts') || 'Create default shifts'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      
      <div className="calendar-table-container">
        <table className="calendar-table">
          <thead>
            <tr>
              <th className="employee-header">{t('planning.employee_header')}</th>
              {weekDays.map(day => (
                <th key={day.toISOString()} className="day-header">
                  <div className="day-name">
                    {day.toLocaleDateString(dateLocale, { weekday: 'short' })}
                  </div>
                  <div className="day-number">
                    {day.toLocaleDateString(dateLocale, { day: 'numeric' })}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {employees.length === 0 ? (
              <tr>
                <td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.6)' }}>
                  {t('planning.no_active_employees')}
                </td>
              </tr>
            ) : (
              filteredEmployees.map(employee => (
                <tr key={employee.id}>
                  <td className="employee-cell">
                    <div className="employee-name">{employee.firstName || employee.first_name} {employee.lastName || employee.last_name}</div>
                    {employee.department && (
                      <small className="employee-department">{translateDepartment(employee.department)}</small>
                    )}
                  </td>
                  {weekDays.map(day => {
                    const shift = getShiftForEmployeeAndDate(employee.id, day);
                    const dateStr = toLocalDateString(day);
                    const isSelected = multiSelectMode && selectedEmployee === employee.id && selectedDates.some(d => toLocalDateString(d) === dateStr);
                    const isSelectable = multiSelectMode || !shift;
                    
                    return (
                      <td 
                        key={`${employee.id}-${dateStr}`} 
                        className="shift-cell"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!canEdit) return; // Mode lecture seule pour employés
                          if (multiSelectMode) {
                            handleAddShift(employee.id, day);
                          } else if (shift) {
                            handleAddShift(employee.id, day, shift); // Clic sur shift existant (M, J, AM, N) → ouvrir le modal (form update)
                          } else {
                            handleAddShift(employee.id, day);
                          }
                        }}
                        style={{
                          background: isSelected ? 'rgba(59, 130, 246, 0.3)' : 'transparent',
                          border: isSelected ? '2px solid #3B82F6' : undefined,
                          position: 'relative',
                          cursor: canEdit ? 'pointer' : 'default'
                        }}
                      >
                        {shift ? (
                          <div 
                            className="shift-badge"
                            style={{ backgroundColor: shift.shift_color || '#3B82F6' }}
                            title={t('planning.shift_time_range').replace('{{name}}', getShiftDisplayName({ code: shift.shift_code, name: shift.shift_name })).replace('{{start}}', shift.shift_start).replace('{{end}}', shift.shift_end)}
                          >
                            <div className="shift-code">{shift.shift_code}</div>
                            <div className="shift-time">
                              {shift.shift_start} - {shift.shift_end}
                            </div>
                            {shift.confirmed_by_employee && (
                              <span className="confirmed">✓</span>
                            )}
                            {canEdit && (
                              <button
                                className="delete-shift-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteShift(shift.id);
                                }}
                                title={t('planning.delete')}
                              >
                                ×
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="empty-shift" title={canEdit ? t('planning.click_to_add') : (t('planning.read_only') || 'Consultation uniquement')}>
                            {canEdit ? '+' : ''}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal de sélection de type de shift */}
      {showShiftModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
          onClick={() => { setShowShiftModal(false); setEditingAssignment(null); }}
        >
          <div 
            style={{
              background: 'linear-gradient(180deg, #1a1f3a 0%, #0f1419 100%)',
              borderRadius: '20px',
              padding: '32px',
              maxWidth: '500px',
              width: '100%',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ 
              color: '#fff', 
              marginTop: 0, 
              marginBottom: '24px',
              fontSize: '24px',
              fontWeight: 700
            }}>
              {t('planning.choose_shift_type')}
              {multiSelectMode && selectedDates.length > 0 && (
                <div style={{ fontSize: '14px', fontWeight: 400, opacity: 0.8, marginTop: '8px' }}>
                  ({t('planning.days_selected').replace('{{count}}', selectedDates.length)})
                </div>
              )}
            </h3>
            
            <div style={{ marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {shiftTypes.map(st => (
                <button
                  key={st.id}
                  onClick={() => handleSelectShiftType(st.id)}
                  style={{
                    padding: '16px 20px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: `2px solid ${st.color || '#3B82F6'}`,
                    borderRadius: '12px',
                    color: '#fff',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    e.currentTarget.style.transform = 'translateX(4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                >
                  <div 
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      background: st.color || '#3B82F6',
                      flexShrink: 0
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: '16px', marginBottom: '4px' }}>
                      {getShiftDisplayName(st)} ({st.code})
                    </div>
                    <div style={{ fontSize: '14px', opacity: 0.8 }}>
                      {st.start_time} - {st.end_time}
                    </div>
                  </div>
                </button>
              ))}
            </div>
            
            <button
              onClick={() => { setShowShiftModal(false); setEditingAssignment(null); }}
              style={{
                width: '100%',
                padding: '12px 24px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '10px',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '15px',
                fontWeight: 600,
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
            >
              {t('planning.cancel')}
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default TeamCalendar;

